import * as THREE from 'three';
import { MODELS_BASE } from '@/hero/preload';
import type { LayerBatch } from '@/render/batch';
import { sun, useSunStore } from '@/render/sun';
import type { LayerRig } from '@/render/rig';
import { hero, useTourStore } from '@/state/tour';
import { STORE_LAYER } from '@/hero/StoreLayer';
import { useHeroStore } from '@/store/store';
import { layersOf, resetLayers, type Frame, type StationImpl } from '../types';
import { instant, localMinutes } from './clock';
import { WorldPanel } from './Panel';
import { WorldScene } from './Scene';
import { SITE, useWorldStore } from './state';
import { sunDirection, sunPosition, sunTimes } from './sun';

// the shadow camera covers the hero and this much around it
const SHADOW_MARGIN = 60;
// with the scroll, the day runs from an hour after sunrise to an hour before sunset
const DAY_INSET = 60;
const NOON_COLOR = new THREE.Color('#fff4e0');
const LOW_COLOR = new THREE.Color('#ffb877');

const casting = new Set<LayerBatch>();
const box = new THREE.Box3();
let storeSize = -1;

export const world: StationImpl = {
  pick: 'arch',
  enter(frame) {
    const { layers, height } = frame;
    resetLayers(layers, height);
    for (const h of layersOf(layers, (d) => d === 'mep')) h.rig.setVisible(false);
    place(frame);
    for (const h of layers.values()) castShadows(h.rig);
    storeSize = -1;
    useWorldStore.getState().setMinutes(useWorldStore.getState().minutes, true);
    useSunStore.getState().setOn(true);
    if (hero.ifc) useHeroStore.getState().load(MODELS_BASE + hero.ifc);
  },
  update(frame) {
    const store = frame.layers.get(STORE_LAYER)?.rig;
    const size = store?.elements.size ?? 0;
    if (store && size !== storeSize) {
      castShadows(store);
      storeSize = size;
    }
    place(frame);
    const w = useWorldStore.getState();
    const { lat, lon } = w.origin;
    const tz = SITE.timeZone;
    if (w.follow && !useTourStore.getState().reducedMotion) {
      const { sunrise, sunset } = sunTimes(lat, lon, instant(w.date, 12 * 60, tz));
      const from = sunrise ? localMinutes(sunrise, w.date, tz) + DAY_INSET : 8 * 60;
      const to = sunset ? localMinutes(sunset, w.date, tz) - DAY_INSET : 17 * 60;
      w.setMinutes(Math.round((from + (to - from) * frame.t) / 5) * 5, true);
    }
    const { minutes } = useWorldStore.getState();
    const position = sunPosition(lat, lon, instant(w.date, minutes, tz));
    const [x, y, z] = sunDirection(position);
    sun.direction.set(x, y, z).applyAxisAngle(THREE.Object3D.DEFAULT_UP, w.yaw);
    sun.target.set(hero.position[0], w.groundY + w.height / 2, hero.position[2]);
    sun.radius = w.radius + SHADOW_MARGIN;
    const day = THREE.MathUtils.smoothstep(position.altitude, -1, 8);
    sun.intensity = 3.2 * day;
    sun.color.lerpColors(
      LOW_COLOR,
      NOON_COLOR,
      THREE.MathUtils.smoothstep(position.altitude, 5, 35),
    );
  },
  leave({ layers, height }) {
    for (const b of casting) b.castShadow = b.receiveShadow = false;
    casting.clear();
    useSunStore.getState().setOn(false);
    resetLayers(layers, height);
  },
  Panel: WorldPanel,
  Scene: WorldScene,
};

function castShadows(rig: LayerRig): void {
  for (const el of rig.elements.values())
    for (const { batch } of el.instances) {
      if (casting.has(batch)) continue;
      batch.castShadow = batch.receiveShadow = true;
      casting.add(batch);
    }
}

// Ground at the model's zero (the hero stands on its lowest point), the site turned by the hero's
// own turn plus the IFC's true north.
function place({ layers, height }: Frame): void {
  box.makeEmpty();
  for (const h of layers.values()) box.union(h.box);
  if (box.isEmpty()) return;
  const groundY = Math.max(0, -box.min.y);
  const trueNorth = useHeroStore.getState().site?.trueNorth ?? 0;
  useWorldStore.getState().setPlacement({
    groundY,
    yaw: hero.rotationY + THREE.MathUtils.degToRad(trueNorth),
    height: Math.max(0, height - groundY),
    radius: Math.hypot(box.max.x - box.min.x, box.max.z - box.min.z) / 2,
  });
}
